import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AuthGate from "@/components/AuthGate";
import { GeoBackdrop } from "@/components/Brand";
import { useUser } from "@/lib/userContext";
import api from "@/lib/api";

const TYPE_META = {
    client: { label: "For Brands", color: "Application" },
    doctor: { label: "Dr. Aangan Theory", color: "Brand Diagnosis" },
    academy: { label: "Aangan Academy", color: "Academy" },
    career: { label: "Careers", color: "Career Application" },
};

function StatusPill({ status, paid }) {
    return (
        <div className="flex gap-2 items-center flex-wrap">
            <span className="tag">{status}</span>
            {paid === "paid" && <span className="tag is-active">Paid</span>}
        </div>
    );
}

function DashboardBody() {
    const { user } = useUser();
    const [rows, setRows] = useState(null);

    useEffect(() => {
        api
            .get("/me/applications")
            .then((r) => setRows(r.data))
            .catch(() => setRows([]));
    }, []);

    return (
        <>
            <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-12 border-b border-white/8 overflow-hidden">
                <GeoBackdrop />
                <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5 text-saffron-brand">My Dashboard</div>
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
                        <div>
                            <h1 className="font-display text-4xl lg:text-6xl tracking-tighter leading-[0.95] mb-3">
                                Welcome, <span className="text-saffron-brand">{user.name?.split(" ")[0] || "there"}</span>.
                            </h1>
                            <p className="text-white/65 text-base lg:text-lg max-w-xl">
                                All your Aangan Theory applications, diagnoses and subscriptions — in one place.
                            </p>
                        </div>
                        <div className="card-surface p-4 lg:p-5 flex items-center gap-4">
                            {user.picture && (
                                <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />
                            )}
                            <div>
                                <div className="text-white text-sm">{user.name}</div>
                                <div className="text-white/45 text-xs">{user.email}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 lg:py-24">
                <div className="max-w-6xl mx-auto px-6 lg:px-10">
                    {rows === null ? (
                        <div className="py-20 grid place-items-center">
                            <Loader2 className="animate-spin text-saffron" size={20} />
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-8">
                                <h2 className="font-display text-2xl tracking-tight">Your applications</h2>
                                <div className="text-white/45 text-sm" data-testid="my-app-count">{rows.length} total</div>
                            </div>

                            {rows.length === 0 ? (
                                <div className="card-surface p-12 lg:p-16 text-center" data-testid="my-empty-state">
                                    <div className="eyebrow mb-3">Nothing here yet</div>
                                    <h3 className="font-display text-2xl mb-4">Start your growth journey</h3>
                                    <p className="text-white/55 mb-8 max-w-md mx-auto text-sm">
                                        Pick a pillar and submit your first application — it will appear here automatically.
                                    </p>
                                    <div className="flex flex-wrap justify-center gap-3">
                                        <Link to="/clients" className="btn-primary" data-testid="my-cta-clients">
                                            For Brands <ArrowUpRight size={16} />
                                        </Link>
                                        <Link to="/doctor" className="btn-secondary" data-testid="my-cta-doctor">
                                            Dr. Aangan Theory <ArrowUpRight size={16} />
                                        </Link>
                                        <Link to="/academy" className="btn-secondary" data-testid="my-cta-academy">
                                            Aangan Academy <ArrowUpRight size={16} />
                                        </Link>
                                        <Link to="/careers" className="btn-secondary" data-testid="my-cta-careers">
                                            Careers <ArrowUpRight size={16} />
                                        </Link>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-3">
                                    {rows.map((r) => {
                                        const meta = TYPE_META[r.type] || { label: r.type, color: "Application" };
                                        return (
                                            <Link
                                                key={r.tracking_id}
                                                to={`/status/${r.tracking_id}`}
                                                data-testid={`my-app-${r.tracking_id}`}
                                                className="card-surface p-6 lg:p-7 group grid grid-cols-1 md:grid-cols-12 gap-6 items-center"
                                            >
                                                <div className="md:col-span-3">
                                                    <div className="eyebrow mb-1">{meta.color}</div>
                                                    <div className="font-mono text-sm text-saffron-brand">{r.tracking_id}</div>
                                                </div>
                                                <div className="md:col-span-3 font-display text-base text-white">{meta.label}</div>
                                                <div className="md:col-span-4">
                                                    <StatusPill status={r.status} paid={r.payment_status} />
                                                </div>
                                                <div className="md:col-span-2 flex md:justify-end items-center gap-2 text-sm text-white/65 group-hover:text-saffron-brand">
                                                    View
                                                    <ArrowUpRight size={14} />
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </section>
        </>
    );
}

export default function MyDashboard() {
    return (
        <main className="min-h-screen" data-testid="my-dashboard">
            <Navbar />
            <AuthGate title="Sign in to view your dashboard" subtitle="Use Google to access your applications, diagnoses and subscriptions.">
                <DashboardBody />
            </AuthGate>
            <Footer />
        </main>
    );
}
